import React from 'react';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import { decamelizeKeys } from 'humps';
import { object, array, string } from 'yup';
import { Button, Row, Container, Col } from 'reactstrap';

import api from '../../../lib/api';
import Remark from './Remark';
import endpoints from '../../../lib/endpoints';
import Passenger from './Passenger';
import ErrorFocus from './ErrorFocus';
import ContactPerson from './ContactPerson';
import FormikPersist from './FormikPersist';
import { getFrontUrl } from '../helpers';
import MessageCheckbox from './MessageCheckbox';
import { fetchCountries } from '../../../redux/actions/FlightsUiActions';
import { getCustomToast } from '../../../components/ErrorMessage/Toast';
import { useDocumentTitle } from '../../../hooks';
import { SemiTransparentLoader, WhitePanel } from '../../../components';
import {
  injectFlightSearchParams,
  ReservationTimeExpiredModal,
  FlightSession,
} from '../Common';

import './passengerdetail.css';

const PassengerDetailsScreen = props => {
  useDocumentTitle('Flights Passenger Details');

  const { location, history, queryParams, isOneWay, sessionId } = props;
  const { search, pathname } = location;
  const { numAdults, numChildren } = queryParams;

  const [isLoading, setIsLoading] = React.useState(null);

  React.useEffect(() => {
    props.fetchCountries();
  }, []);

  async function generateInvoiceAndPushUrl(values) {
    setIsLoading(true);

    try {
      const { data } = await api.postWithoutToken(
        endpoints.flights.getInvoices,
        getData(values, isOneWay, sessionId),
      );

      history.push({
        pathname: `${getFrontUrl(pathname)}/details-review/${data.invoices[0]}`,
        search,
      });
    } catch (err) {
      getCustomToast('There seems to be a problem. Please try again', 'error');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <ReservationTimeExpiredModal>
      <SemiTransparentLoader
        isLoading={isLoading}
        loadingText="Generating your invoice"
      />
      <Formik
        initialValues={{
          ...contactPersonObj,
          passengerDetails: [...createPassengers(numAdults, numChildren)],
          remarks: '',
          shouldAgreeToTermsAndConditions: false,
        }}
        validationSchema={PassengerDetailSchema}
        validate={validate}
        onSubmit={generateInvoiceAndPushUrl}
      >
        {({ values, handleSubmit, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Container>
              <Row>
                <Container>
                  <ContactPerson />
                  <WhitePanel>
                    <h2>PASSENGER DETAIL</h2>
                    {values.passengerDetails.map((_, i) => (
                      <Passenger name="passengerDetails" id={i} key={i} />
                    ))}
                    <Remark name="remarks" />
                  </WhitePanel>
                  <Row className="my-2">
                    <Col lg="10" className="text-left terms-conditions">
                      <MessageCheckbox name="shouldAgreeToTermsAndConditions" />
                    </Col>
                    <Col className="text-right">
                      <Button type="submit" color="yellow" id="flights-continue-to-flight-review">
                        CONTINUE
                      </Button>
                    </Col>
                    <br />
                  </Row>
                </Container>
              </Row>
            </Container>
            <ErrorFocus />
            <FormikPersist name="PASSENGER_DETAILS" />
          </form>
        )}
      </Formik>
    </ReservationTimeExpiredModal>
  );
};

function getData(values, isOneWay, sessionId) {
  const {
    passengerDetails,
    firstName,
    lastName,
    contactMobile,
    contactEmail,
    remarks,
  } = values;

  const contactPerson = {
    contactName: `${firstName} ${lastName}`,
    contactMobile,
    contactEmail,
  };

  const inbound = FlightSession.getInbound();
  const outbound = FlightSession.getOutbound();

  const passengerDetailsWithRemarks = appendRemarksToLast(
    decamelizeKeys(passengerDetails),
    remarks
  );

  return {
    flights: [
      {
        passenger_detail: passengerDetailsWithRemarks,
        flight_id: outbound.flightId,
        ...(!isOneWay ? { return_flight_id: inbound.flightId } : {}),
        ...decamelizeKeys(contactPerson),
        session_id: sessionId,
      },
    ],
  };
}

function validate(values) {
  let errors = {};
  const { shouldAgreeToTermsAndConditions } = values;

  if (!shouldAgreeToTermsAndConditions) {
    errors.shouldAgreeToTermsAndConditions =
      'Please accept the terms and conditions to proceed.';
  }

  return errors;
}

function appendRemarksToLast(passengerDetails, remarks) {
  const passengerNum = passengerDetails.length;

  return passengerDetails.map((passenger, idx) => {
    const forLastPassenger = passengerNum === idx + 1;
    if (forLastPassenger) {
      // append remark for the last passenger
      return { ...passenger, remarks };
    }

    return { ...passenger, remarks: '' };
  });
}

const contactPersonObj = {
  firstName: '',
  lastName: '',
  contactEmail: '',
  contactMobile: '',
};

const passengerDetails = (isAdult: boolean) => ({
  gender: 'F',
  firstName: '',
  lastName: '',
  nationality: '',
  // remarks: '',
  title: 'Ms',
  paxType: isAdult ? 'Adult' : 'Child',
});

const createPassengers = (adults, children) => {
  const isAdult = true;
  const isChild = !isAdult;
  return [
    ...(adults > 0
      ? Array.from({ length: adults }, () => passengerDetails(isAdult))
      : []),
    ...(children > 0
      ? Array.from({ length: children }, () => passengerDetails(isChild))
      : []),
  ];
};

const makeErrorMessageFor = str => `Please enter your ${str}`;

const emailAddressErrorMessage =
  'Please enter your email address in format: yourname@example.com';

const PassengerDetailSchema = object().shape({
  firstName: string().required(makeErrorMessageFor('first name')),
  lastName: string().required(makeErrorMessageFor('last name')),
  contactEmail: string()
    .email(emailAddressErrorMessage)
    .required(emailAddressErrorMessage),
  contactMobile: string().required(makeErrorMessageFor('contact number')),
  passengerDetails: array().of(
    object().shape({
      firstName: string().required(makeErrorMessageFor('first name')),
      lastName: string().required(makeErrorMessageFor('last name')),
      gender: string().required('Please select one'),
      nationality: string().required('Please select one'),
    })
  ),
});

const mapStateToProps = (state) => ({
  sessionId: state.flights.sessionId,
});

export default connect(
  mapStateToProps,
  { fetchCountries }
)(injectFlightSearchParams(PassengerDetailsScreen));



// WEBPACK FOOTER //
// ./src/screens/Flights/PassengerDetails/PassengerDetailsScreen.js